import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { toast } from "sonner";
import LedgerModal from "@/components/LedgerModal";

export default function AdminPayments() {
  const [balances, setBalances] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pay, setPay] = useState(null);
  const [ledger, setLedger] = useState(null);

  const load = useCallback(async () => {
    const [b, p] = await Promise.all([api.get("/admin/pro-balances"), api.get("/admin/pro-payments")]);
    setBalances(b.data); setPayments(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!balances) return <Loader />;

  return (
    <div>
      <PageTitle>Paiements des professionnels</PageTitle>
      <Panel className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Professionnel</th><th className="text-right px-4 py-3">Gagné</th>
            <th className="text-right px-4 py-3">Payé</th><th className="text-right px-4 py-3">Solde dû</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {balances.map((b) => (
              <tr key={b.user_id} className="border-t border-slate-100" data-testid={`balance-${b.user_id}`}>
                <td className="px-4 py-3"><p className="font-semibold">{b.name}</p><p className="text-xs text-slate-400">{(b.skills || []).join(", ")}</p></td>
                <td className="px-4 py-3 text-right font-num">{formatFCFA(b.earned)}</td>
                <td className="px-4 py-3 text-right font-num text-green-600">{formatFCFA(b.paid)}</td>
                <td className={`px-4 py-3 text-right font-num font-bold ${b.remaining > 0 ? "text-[#C85A32]" : "text-slate-400"}`}>{formatFCFA(b.remaining)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setLedger(b)} className="border border-slate-200 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-lg mr-2" data-testid={`ledger-btn-${b.user_id}`}>Grand livre</button>
                  <button onClick={() => setPay(b)} className="bg-[#0F172A] text-white text-xs font-bold px-3 py-1.5 rounded-lg" data-testid={`pay-btn-${b.user_id}`}>Enregistrer paiement</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <h2 className="font-display text-lg font-bold mb-2">Historique des paiements</h2>
      <div className="space-y-2">
        {payments.length === 0 && <p className="text-sm text-slate-400">Aucun paiement enregistré.</p>}
        {payments.map((p) => (
          <Panel key={p.id} className="p-3 flex items-center gap-3" data-testid={`payment-row-${p.id}`}>
            <div className="flex-1"><p className="font-semibold text-sm">{p.professional_name}</p><p className="text-xs text-slate-400">{formatDate(p.date)} · {p.method} {p.reference && `· ${p.reference}`} · par {p.paid_by}</p></div>
            <span className="font-num font-bold text-[#C85A32]">{formatFCFA(p.amount)}</span>
          </Panel>
        ))}
      </div>

      {pay && <PayModal balance={pay} onClose={() => setPay(null)} onDone={load} />}
      {ledger && <LedgerModal proId={ledger.user_id} name={ledger.name} onClose={() => setLedger(null)} />}
    </div>
  );
}

function PayModal({ balance, onClose, onDone }) {
  const [f, setF] = useState({ amount: balance.remaining, method: "espece", reference: "", date: new Date().toISOString().slice(0, 10) });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/pro-payments", { professional_id: balance.user_id, ...f, amount: Number(f.amount) }); toast.success("Paiement enregistré"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`Paiement — ${balance.name}`} onClose={onClose} testid="pay-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-500">Solde dû : <strong className="text-[#C85A32]">{formatFCFA(balance.remaining)}</strong></p>
        <TextField label="Montant (FCFA)" type="number" value={f.amount} onChange={set("amount")} testid="pay-amount" />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Mode de paiement" value={f.method} onChange={set("method")} testid="pay-method" placeholder={null}
            options={[{ value: "espece", label: "Espèces" }, { value: "mobile_money", label: "Mobile Money" }, { value: "virement", label: "Virement" }, { value: "cheque", label: "Chèque" }]} />
          <TextField label="Date" type="date" value={f.date} onChange={set("date")} testid="pay-date" />
        </div>
        <TextField label="Référence" value={f.reference} onChange={set("reference")} testid="pay-ref" required={false} />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="pay-submit">Enregistrer le paiement</button>
      </form>
    </Modal>
  );
}
